import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function BillingDashboard() {
  const [currentUsage, setCurrentUsage] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [usageHistory, setUsageHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      // Fetch current usage from instances
      const instancesRes = await fetch(`${API_URL}/v1/instances`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const instancesData = await instancesRes.json();
      
      // Calculate current month usage
      const instances = instancesData.instances || [];
      const totalCost = instances.reduce((sum, inst) => sum + (inst.cost_per_hour || 0), 0);
      const hoursInMonth = 730; // Average hours per month
      const projectedMonthly = totalCost * hoursInMonth;

      setCurrentUsage({
        active_instances: instances.filter(i => i.status === 'running').length,
        total_instances: instances.length,
        hourly_cost: totalCost,
        projected_monthly: projectedMonthly,
        current_month_to_date: projectedMonthly * (new Date().getDate() / 30)
      });

      // Mock invoice history (in production, fetch from API)
      setInvoices([
        { id: '1', month: 'January 2026', amount: 45.67, status: 'paid', date: '2026-02-01' },
        { id: '2', month: 'December 2025', amount: 52.34, status: 'paid', date: '2026-01-01' },
        { id: '3', month: 'November 2025', amount: 38.90, status: 'paid', date: '2025-12-01' },
      ]);

      // Mock usage history
      setUsageHistory([
        { date: 'Feb 20', instances: 3, cost: 12.50 },
        { date: 'Feb 21', instances: 3, cost: 12.50 },
        { date: 'Feb 22', instances: 5, cost: 20.80 },
        { date: 'Feb 23', instances: 5, cost: 20.80 },
        { date: 'Feb 24', instances: 4, cost: 16.60 },
        { date: 'Feb 25', instances: 4, cost: 16.60 },
        { date: 'Feb 26', instances: 6, cost: 24.90 },
      ]);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching billing data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading billing data...</div>
      </div>
    );
  }

  return (
    <Layout userType="customer">
      <div className="max-w-7xl mx-auto p-6">
        <h1 className="text-3xl font-bold text-white mb-6">Billing & Usage</h1>

        {/* Current Usage Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <div className="text-gray-400 text-sm mb-2">Current Hourly Rate</div>
            <div className="text-3xl font-bold text-white">
              ${currentUsage?.hourly_cost.toFixed(2)}/hr
            </div>
          </div>

          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <div className="text-gray-400 text-sm mb-2">Projected Monthly</div>
            <div className="text-3xl font-bold text-blue-400">
              ${currentUsage?.projected_monthly.toFixed(2)}
            </div>
          </div>

          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <div className="text-gray-400 text-sm mb-2">Month to Date</div>
            <div className="text-3xl font-bold text-green-400">
              ${currentUsage?.current_month_to_date.toFixed(2)}
            </div>
          </div>

          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <div className="text-gray-400 text-sm mb-2">Active Instances</div>
            <div className="text-3xl font-bold text-white">
              {currentUsage?.active_instances}
            </div>
          </div>
        </div>

        {/* Usage Chart */}
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Usage Trends (Last 7 Days)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={usageHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9CA3AF" />
              <YAxis yAxisId="left" stroke="#9CA3AF" />
              <YAxis yAxisId="right" orientation="right" stroke="#9CA3AF" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
                labelStyle={{ color: '#F3F4F6' }}
              />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="instances" stroke="#60A5FA" name="Instances" strokeWidth={2} />
              <Line yAxisId="right" type="monotone" dataKey="cost" stroke="#34D399" name="Daily Cost ($)" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Cost Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Cost Breakdown</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Compute Instances</span>
                <span className="text-white font-medium">${(currentUsage?.projected_monthly * 0.7).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Block Storage</span>
                <span className="text-white font-medium">${(currentUsage?.projected_monthly * 0.2).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Object Storage</span>
                <span className="text-white font-medium">${(currentUsage?.projected_monthly * 0.1).toFixed(2)}</span>
              </div>
              <div className="border-t border-gray-700 pt-3 flex justify-between items-center">
                <span className="text-white font-bold">Total Projected</span>
                <span className="text-green-400 font-bold text-xl">
                  ${currentUsage?.projected_monthly.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Payment Method</h2>
            <div className="bg-gray-700 p-4 rounded-lg mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-12 h-8 bg-blue-600 rounded mr-3 flex items-center justify-center text-white font-bold">
                    VISA
                  </div>
                  <div>
                    <div className="text-white font-medium">•••• •••• •••• 4242</div>
                    <div className="text-gray-400 text-sm">Expires 12/2027</div>
                  </div>
                </div>
                <button className="text-blue-400 hover:text-blue-300 text-sm">
                  Update
                </button>
              </div>
            </div>
            <div className="text-gray-400 text-sm">
              💡 Payments are processed automatically on the 1st of each month
            </div>
          </div>
        </div>

        {/* Invoice History */}
        <div className="bg-gray-800 rounded-lg border border-gray-700">
          <div className="p-6 border-b border-gray-700">
            <h2 className="text-xl font-bold text-white">Invoice History</h2>
          </div>
          <table className="min-w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td className="px-6 py-4 text-white font-medium">{invoice.month}</td>
                  <td className="px-6 py-4 text-white">${invoice.amount}</td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 rounded text-xs bg-green-900 text-green-300">
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-400">
                    {new Date(invoice.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <button className="text-blue-400 hover:text-blue-300 text-sm">
                      Download PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export default BillingDashboard;
